/*  =======================================================================
    src/Pages/Invoices.js  •  v2.7  (2025-07-13)
    -----------------------------------------------------------------------
    + Adds pseudo-release “General Costs” (releaseId === null)
      – appears first in the release dropdown
      – task picker loads tasks where releaseId is null
      – POST sends releaseId:null instead of "null"
    --------------------------------------------------------------------- */

    import React, { useState, useEffect } from 'react';
    import InvoiceDatabase from '../Components/InvoiceDatabase';
    import { api } from '../api';
    
    /* synthetic release row */
    const GENERAL_RELEASE = { _id: 'null', title: 'General Costs' };
    
    /* --------------------------------------------------
       1) INVOICES FROM MONGODB
       -------------------------------------------------- */
    const Invoices = ({ projects = [], releases = [] }) => {
      const [invoices,        setInvoices]        = useState([]);
      const [loadingInvoices, setLoadingInvoices] = useState(true);
      const [fetchError,      setFetchError]      = useState('');
      // --- NEW: server-side paging + processed toggle ---
const [showProcessed, setShowProcessed] = useState(false);
const [page, setPage] = useState(1);
const [limit] = useState(50);
const [total, setTotal] = useState(0);
const [loadingPage, setLoadingPage] = useState(false);
    
      const FRONTEND_URL =
        process.env.REACT_APP_FRONTEND_URL || 'http://localhost:3000';
    // Fetch a slim index of ALL releases for typeahead filtering (not just the paged subset)
  const fetchAllReleasesForFilter = async () => {
    try {
      // Backend caps /api/releases/paged limit to 100, so requesting >100
      // can silently truncate results and break pagination.
      const pageSize = 100;
      let page = 1;
      let total = 0;
      const acc = [];

      do {
        const qs = new URLSearchParams({
          limit: String(pageSize),
          page: String(page),
          // keep it slim for speed
          fields: 'title,projectId,coverArt'
        });
        const r = await api(`/api/releases/paged?${qs.toString()}`);
        if (!r.ok) throw new Error(`releases paged ${r.status}`);
        const data = await r.json(); // { items, total }

        if (Array.isArray(data.items)) acc.push(...data.items);
        total = Number(data.total || 0);
        page += 1;
      } while ((page - 1) * pageSize < total);

      setReleasesForFilter(acc);
    } catch (e) {
      console.error('[Invoices] full releases index failed:', e);
      // fall back: keep whatever parent passed via props
      setReleasesForFilter([]);
    }
  };
      // Fetch a page from the backend with optional excludeProcessed
const fetchInvoicesPage = async ({ pageArg = 1, append = false, processed = showProcessed } = {}) => {
  try {
    setLoadingPage(true);
    setFetchError('');
    const qs = new URLSearchParams({
      page: String(pageArg),
      limit: String(limit),
      ...(processed ? {} : { excludeProcessed: '1' }),
    });

    const r = await api(`/api/invoices?${qs.toString()}`);
    if (!r.ok) throw new Error(`Status ${r.status}`);

    const data = await r.json();
    // Backward-compat: if server sent array, treat as single-page
    const items = Array.isArray(data) ? data : (data.items || []);
    const totalCount = Array.isArray(data) ? items.length : Number(data.total || 0);

    setInvoices(prev => (append ? [...prev, ...items] : items));
    setTotal(totalCount);
    setPage(pageArg);
  } catch (e) {
    setFetchError(e.message || 'Failed to fetch invoices');
  } finally {
    setLoadingPage(false);
  }
};
// Reload current view from page 1 (respecting Show Processed)
const reloadInvoices = async () => {
  await fetchInvoicesPage({ pageArg: 1, append: false, processed: showProcessed });
};
// Initial load: page 1, exclude processed by default
useEffect(() => {
  (async () => {
    try {
      await fetchInvoicesPage({ pageArg: 1, append: false, processed: false });
    } finally {
      // no matter success/fail, stop showing the big spinner
      setLoadingInvoices(false);
    }
  })();

  // keep your releases index fetch as-is; it runs in parallel
  fetchAllReleasesForFilter();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

        
    
      /* --------------------------------------------------
         2) WIZARD / MODAL STATE
         -------------------------------------------------- */
      const [showModal, setShowModal] = useState(false);
      const [step,      setStep]      = useState(1);
      const [loading,   setLoading]   = useState(false);
      const [error,     setError]     = useState('');
      const [aiResponse,    setAIResponse]    = useState('');
      const [extractedText, setExtractedText] = useState('');
      const [showOCRText,   setShowOCRText]   = useState(false);
    
      const [file, setFile] = useState(null);
    
      /* invoice / link fields */
      const [description,        setDescription]        = useState('');
      const [invoiceAmount,      setInvoiceAmount]      = useState('');
      const [invoiceCost,        setInvoiceCost]        = useState('');
      const [paymentStatus,      setPaymentStatus]      = useState(null);
      const [paymentMethod,      setPaymentMethod]      = useState('');
      const [companyName,        setCompanyName]        = useState('');
      const [paymentInformation, setPaymentInformation] = useState('');
      const [fileName,           setFileName]           = useState('');
    
      /* link → artist / project / release / task */
      const [allArtists, setAllArtists] = useState([]);
      const [searchArtistTerm, setSearchArtistTerm] = useState('');
      const [selectedArtist, setSelectedArtist] = useState(null);
      const [artistFocus, setArtistFocus] = useState(false);
      
      const [searchTerm,        setSearchTerm]        = useState('');
      const [selectedProject,   setSelectedProject]   = useState(null);
      const [searchReleaseTerm, setSearchReleaseTerm] = useState('');
      const [selectedRelease,   setSelectedRelease]   = useState(null);
    
const [tasksForRelease, setTasksForRelease] = useState([]);
const [loadingTasks,    setLoadingTasks]    = useState(false);
const [searchTaskTerm,  setSearchTaskTerm]  = useState('');
const [selectedTask,    setSelectedTask]    = useState(null);

// inline task creation
const [newTaskName,     setNewTaskName]     = useState('');
const [creatingTask,    setCreatingTask]    = useState(false);
const [createTaskError, setCreateTaskError] = useState('');
    
      /* dropdown focus flags */
      const [projFocus, setProjFocus] = useState(false);
      const [relFocus,  setRelFocus]  = useState(false);
      const [taskFocus, setTaskFocus] = useState(false);
      
      /* fetch artists */
      useEffect(() => {
        (async () => {
          try {
            const res = await api('/api/artists');
            if (!res.ok) throw new Error(`Failed to fetch artists (${res.status})`);
            setAllArtists(await res.json());
          } catch (e) {
            console.error('[Invoices] fetch artists failed:', e);
          }
        })();
      }, []);
    
      /* logged-in user (localStorage) */
      const storedEmail = localStorage.getItem('email') || '';
      const storedName  = localStorage.getItem('name')  || '';
    
      /* email */
      const [recipient,    setRecipient]    = useState('finance@cldkid.com');
      const [cc,           setCc]           = useState(storedEmail);
      const [emailSubject, setEmailSubject] = useState('Invoice');
      const [emailBody,    setEmailBody]    = useState(
        'Hello,\nPlease find attached the invoice.\n\nRegards'
      );
      const [emailSending, setEmailSending] = useState(false);

        const [releasesForFilter, setReleasesForFilter] = useState([]);
    
      const costsOptions = [
        { label:'Recoupable',                 color:'#9eeb68' },
        { label:'Recoupable (-Side Artists)', color:'orange'  },
        { label:'Non-Recoupable',             color:'#ff4c4c'},
        { label:'Advance',                    color:'#777777'},
      ];
    
      /* --------------------------------------------------
         3) FILTER HELPERS (10 recent if no search)
         -------------------------------------------------- */
      // Filter projects by selected artist first
      const projectsForArtist = selectedArtist
        ? projects.filter(p => {
            const artistIds = Array.isArray(p.artists) ? p.artists : [];
            return artistIds.some(a => {
              const artistId = typeof a === 'object' && a._id ? a._id : a;
              return String(artistId) === String(selectedArtist._id);
            });
          })
        : projects;
      
      const filteredProjects = searchTerm
        ? projectsForArtist.filter(p =>
            p.title?.toLowerCase().includes(searchTerm.toLowerCase()))
        : [...projectsForArtist].slice(-10).reverse();
      
      // Filter artists for dropdown
      const filteredArtists = searchArtistTerm
        ? allArtists.filter(a =>
            (a.name || a.title || '').toLowerCase().includes(searchArtistTerm.toLowerCase()))
        : [...allArtists].slice(-20).reverse();
    
      const getReleaseProjectId = (r) =>
        typeof r.projectId === 'object' && r.projectId ? r.projectId._id : r.projectId;
    
      
    
      /* include General Costs at top — use the full index if available */
const releasesIndex =
  (Array.isArray(releasesForFilter) && releasesForFilter.length)
    ? releasesForFilter
    : releases;

const releasesForProject = selectedProject
  ? releasesIndex.filter(r =>
      String(getReleaseProjectId(r)) === String(selectedProject._id))
  : releasesIndex;

const allReleases = [GENERAL_RELEASE, ...releasesForProject];

const filteredReleases = searchReleaseTerm
  ? allReleases.filter(r =>
      r.title?.toLowerCase().includes(searchReleaseTerm.toLowerCase()))
  : [...allReleases].reverse();

/* keep filteredTasks exactly as-is below */
const filteredTasks = searchTaskTerm
  ? tasksForRelease.filter(t =>
      t.name?.toLowerCase().includes(searchTaskTerm.toLowerCase()))
  : [...tasksForRelease].slice(-10).reverse();
    
      /* --------------------------------------------------
         3a) FETCH TASKS WHEN RELEASE OR PROJECT CHANGES
         -------------------------------------------------- */
      useEffect(() => {
        if (!selectedProject || !selectedRelease) {
          setTasksForRelease([]); setSelectedTask(null); setSearchTaskTerm('');
          return;
        }
        (async () => {
          try {
            setLoadingTasks(true);
            const relParam = selectedRelease._id === 'null' ? 'null' : selectedRelease._id;
            const url = `/api/marketingtasks?projectId=${selectedProject._id}&releaseId=${relParam}`;
            const list = await api(url).then(r => r.json());
            setTasksForRelease(list);
          } catch (e) { console.error(e); setTasksForRelease([]); }
          finally      { setLoadingTasks(false); }
        })();
      }, [selectedProject, selectedRelease]);
    
      /* --------------------------------------------------
         4) MODAL RESET
         -------------------------------------------------- */
      const resetState = () => {
        setStep(1); setFile(null); setError('');
        setAIResponse(''); setExtractedText(''); setShowOCRText(false); setLoading(false);
    
        setDescription(''); setInvoiceAmount(''); setInvoiceCost('');
        setPaymentStatus(null); setPaymentMethod('');
        setCompanyName(''); setPaymentInformation(''); setFileName('');
    
        setSearchArtistTerm(''); setSelectedArtist(null); setArtistFocus(false);
        setSearchTerm(''); setSelectedProject(null); setProjFocus(false);
        setSearchReleaseTerm(''); setSelectedRelease(null); setRelFocus(false);
    
        setTasksForRelease([]); setSearchTaskTerm(''); setSelectedTask(null);
        setTaskFocus(false);
    
        setRecipient('finance@cldkid.com'); setCc(storedEmail);
        setEmailSubject('Invoice');
        setEmailBody('Hello,\nPlease find attached the invoice.\n\nRegards');
      };
      const handleOpenModal  = () => { setShowModal(true);  resetState(); };
      const handleCloseModal = () => { setShowModal(false); resetState(); };
    
      const toggleOCRText = () => setShowOCRText(p=>!p);

      // Create a new marketing task for the currently selected project/release
const handleCreateTask = async () => {
  if (!selectedProject || !selectedRelease) {
    setCreateTaskError('Pick a project and release first.');
    return;
  }

  const name = newTaskName.trim();
  if (!name) {
    setCreateTaskError('Task name is required.');
    return;
  }

  // Duplicate name guard (case-insensitive) within this release
  const alreadyExists = tasksForRelease.some(
    t => (t.name || '').trim().toLowerCase() === name.toLowerCase()
  );
  if (alreadyExists) {
    setCreateTaskError('A task with that name already exists for this release.');
    return;
  }

  setCreatingTask(true);
  setCreateTaskError('');
  try {
    const relId = selectedRelease._id === 'null' ? null : selectedRelease._id;

    const r = await api('/api/marketingtasks', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        projectId: selectedProject._id,
        releaseId: relId,
        name,
      }),
    });
    if (!r.ok) throw new Error(`Create task failed (${r.status})`);
    const created = await r.json();

    // add to list and select immediately
    setTasksForRelease(prev => [created, ...prev]);
    setSelectedTask(created);
    setSearchTaskTerm(created.name || '');
    setNewTaskName('');
  } catch (e) {
    setCreateTaskError(e.message || 'Could not create task');
  } finally {
    setCreatingTask(false);
  }
};
    
      /* --------------------------------------------------
         5) FILE UPLOAD                         (unchanged)
         -------------------------------------------------- */
      const handleFileChange = (e) => setFile(e.target.files[0] || null);
    
      const handleUpload = async () => {
        if (!file){setError('Please select a PDF first.');return;}
        setError(''); setAIResponse(''); setExtractedText('');
        setLoading(true); setStep(1);
        try {
          const fd=new FormData();fd.append('invoice',file);
          const r = await api('/api/invoiceupload/upload-pdf', { method: 'POST', body: fd });
          if(!r.ok)throw new Error('Server error');
          const data=await r.json();
          setAIResponse(data.aiResponse||''); setExtractedText(data.text||'');
          setFileName(data.fileName||'');
          if(data.invoiceAmount) setInvoiceAmount(data.invoiceAmount.replace(/[^\d.]/g,''));
          if(data.orderReference) setDescription(data.orderReference);
          const nm=(data.aiResponse||'').match(/✅\s*Company Name\s*\(([^)]+)\)/i);
          if(nm) setCompanyName(nm[1].trim());
          const pm=(data.aiResponse||'').match(/✅\s*Payment Information\s*\(([^)]+)\)/i);
          if(pm) setPaymentInformation(pm[1].trim());
          setFile(null); setStep(2);
        }catch(e){setError(e.message);}finally{setLoading(false);}
      };
    
      const handleUploadOnly = async () => {
        if(!file){setError('Please select a PDF first.');return;}
        setError(''); setLoading(true); setStep(1);
        try{
          const fd=new FormData();fd.append('invoice',file);
          const r = await api('/api/invoiceupload/upload-pdf?skipExtraction=true', { method: 'POST', body: fd });
          if(!r.ok)throw new Error('Upload error');
          const data=await r.json();
          setFileName(data.fileName||''); setFile(null); setStep(3);
        }catch(e){setError(e.message);}finally{setLoading(false);}
      };
    
      /* --------------------------------------------------
         6) NEXT (create invoice)
         -------------------------------------------------- */
      const handleNext = async () => {
        if(step===2){setStep(3);return;}
    
        if(step===3){
          const finalStatus = paymentStatus==='paid'?'paid':'needs_paid';
          const payload={
            description,
            amount:invoiceAmount,
            costType:invoiceCost,
            paymentStatus:finalStatus,
            fileName,
            projectId:selectedProject?._id||null,
            releaseId:selectedRelease?._id==='null'?null:selectedRelease?._id||null,
            taskId:selectedTask?._id||null,
            uploadDate:new Date().toISOString().split('T')[0],
            paymentMethod:finalStatus==='paid'?paymentMethod:'',
            companyName,paymentInformation,
          };
          try{
            const r=await api('/api/invoices',{
              method:'POST',headers:{'Content-Type':'application/json'},
              body:JSON.stringify(payload),
            });
            if(!r.ok)throw new Error(`Status ${r.status}`);
            const doc=await r.json();
            setInvoices(prev=>[...prev,doc]);
    
            const proj=selectedProject?.title||'No Project';
            const rel=(selectedRelease?._id==='null')
                        ? 'General Costs'
                        : selectedRelease?.title||'No Release';
            const task=selectedTask?.name||'No Task';
            const url=`${FRONTEND_URL}/invoice/${doc._id}`;
            let body=`Hey Gunhild,\n\nFind attached the following invoice:\n\n`;
            body+=`Status: ${finalStatus==='paid'?'Paid':'Needs to be paid'}\n`;
            if(finalStatus==='paid'&&paymentMethod)body+=`Paid via: ${paymentMethod}\n`;
            body+=`Project: ${proj}\nRelease: ${rel}\nTask: ${task}\n\n`;
            body+=`Invoice from: ${companyName||'Unknown'}\n`;
            body+=`Payment Information: ${paymentInformation||'N/A'}\n\n`;
            body+=`View Invoice Details: ${url}\n\nThanks\n\n-- ${storedName}`;
            setEmailSubject(`Invoice - ${description}`); setEmailBody(body);
    
            setStep(4);
          }catch(e){alert(e.message);}
        }
      };
    
      /* --------------------------------------------------
         7) EMAIL SEND / STATUS CHANGE          (unchanged)
         -------------------------------------------------- */
      const handleSendEmail = async () => {
        setEmailSending(true);
        try{
          const payload={recipient,cc,subject:emailSubject,text:emailBody,fileName};
          const r=await api('/api/send-invoice-email',{
            method:'POST',headers:{'Content-Type':'application/json'},
            body:JSON.stringify(payload),
          });
          const res=await r.json();
          if(res.success){alert('Email sent!');handleCloseModal();}
          else alert('Failed: '+res.message);
        }catch(e){alert(e.message);}finally{setEmailSending(false);}
      };
    
      const handleInvoiceStatusChange = async (id,newStatus) => {
        try{
          const r=await api(`/api/invoices/${id}`,{
            method:'PUT',headers:{'Content-Type':'application/json'},
            body:JSON.stringify({paymentStatus:newStatus}),
          });
          if(!r.ok)throw new Error(`Status ${r.status}`);
          const up=await r.json();
          setInvoices(prev=>prev.map(inv=>String(inv._id)===String(id)?up:inv));
        }catch(e){alert(e.message);}
      };
    
      /* --------------------------------------------------
         8) RENDER
         -------------------------------------------------- */
      return (
  <div style={{ padding: 20 }}>
    <h1 className="header" style={{ margin: '10px 0 10px', color: '#fff' }}>
      Invoices
    </h1>


    {/* Upload Invoice (restores your modal entry) */}
    <button
  onClick={handleOpenModal}
  style={{
    background:'#1e1e1e',
    color:'white',
    border:'none',
    padding:'15px 30px',
    borderRadius:8,
    cursor:'pointer',
    marginBottom:40,
    display:'flex',
    alignItems:'center',
    fontWeight:'bold',
  }}
>
  <span style={{ fontSize:24, marginRight:10 }}>+</span>
  Upload Invoice
</button>

    {fetchError && <p style={{ color: 'red' }}>{fetchError}</p>}

    {loadingInvoices ? (
      <p style={{ color: 'white', marginTop: 20 }}>Loading invoices…</p>
    ) : (
      <>
        {/* ========= MODAL ========= */}
        {showModal && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.5)',
              zIndex: 9999,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <div
              style={{
                width: 400,
                background: '#1e1e1e',
                color: 'white',
                borderRadius: 10,
                padding: 20,
                maxHeight: '90vh',
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
              }}
            >
                    {/* ===========================================================
                       STEP 1 — UPLOAD
                       ======================================================= */}
                    {step===1 && !loading && !aiResponse && (
                      <>
                        <h2 style={{ margin:'0 0 10px' }}>Upload Invoice PDF</h2>
                        <input type="file" accept="application/pdf"
                          onChange={handleFileChange}
                          style={{
                            width:'calc(100% - 20px)', padding:10,
                            background:'#333', color:'#fff', border:'none',
                            borderRadius:5, marginBottom:10,
                          }}
                        />
                        <div style={{ display:'flex', justifyContent:'space-between' }}>
                          <button onClick={handleUpload}
                            style={{
                              padding:'10px 20px', background:'#555',
                              color:'#fff', border:'none', borderRadius:5,
                            }}
                          >Upload & Extract</button>
                          <button onClick={handleUploadOnly}
                            style={{
                              padding:'10px 20px', background:'#555',
                              color:'#fff', border:'none', borderRadius:5,
                            }}
                          >Just Upload</button>
                          <button onClick={handleCloseModal}
                            style={{
                              padding:'10px 20px', background:'#555',
                              color:'#fff', border:'none', borderRadius:5,
                            }}
                          >Cancel</button>
                        </div>
                        {error && <p style={{ color:'red' }}>{error}</p>}
                      </>
                    )}
    
                    {/* spinner */}
                    {loading && step===1 && (
                      <div style={{
                        display:'flex', flexDirection:'column', alignItems:'center', gap:10,
                      }}>
                        <h2 style={{ margin:0 }}>Processing…</h2>
                        <div style={{
                          width:40, height:40, border:'4px solid #555',
                          borderTop:'4px solid #fff', borderRadius:'50%',
                          animation:'spin 1s linear infinite',
                        }}/>
                        <style>{`@keyframes spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}`}</style>
                      </div>
                    )}
    
                    {/* ===========================================================
                       STEP 2 — AI ANALYSIS
                       ======================================================= */}
                    {step===2 && aiResponse && !loading && (
                      <>
                        <h2 style={{ margin:'0 0 10px' }}>AI Analysis</h2>
                        <div style={{
                          background:'#333', padding:20, borderRadius:10,
                          whiteSpace:'pre-wrap', marginBottom:10,
                        }}>{aiResponse}</div>
                        <button onClick={toggleOCRText}
                          style={{
                            padding:'10px 20px', background:'#555', color:'#fff',
                            border:'none', borderRadius:5, marginBottom:10,
                          }}
                        >
                          {showOCRText?'Hide OCR Text':'Show OCR Text'}
                        </button>
                        {showOCRText && (
                          <div style={{
                            background:'#333', padding:20, borderRadius:10,
                            whiteSpace:'pre-wrap', marginBottom:10,
                            maxHeight:200, overflowY:'auto',
                          }}>
                            <h3 style={{ marginTop:0 }}>Extracted OCR Text</h3>
                            {extractedText}
                          </div>
                        )}
                        <div style={{ display:'flex', justifyContent:'space-between' }}>
                          <button onClick={handleCloseModal}
                            style={{
                              padding:'10px 20px', background:'#555',
                              color:'#fff', border:'none', borderRadius:5,
                            }}
                          >Close</button>
                          <button onClick={()=>setStep(3)}
                            style={{
                              padding:'10px 20px', background:'#555',
                              color:'#fff', border:'none', borderRadius:5,
                            }}
                          >Next</button>
                        </div>
                      </>
                    )}
    
                    {/* ===========================================================
                       STEP 3 — MANUAL DETAILS
                       ======================================================= */}
                    {step===3 && (
                      <>
                        <h2 style={{ margin:'0 0 10px' }}>Additional Details</h2>
    
                        {/* Description */}
                        <label>Description</label>
                        <input
                          type="text" value={description}
                          onChange={e=>setDescription(e.target.value)}
                          style={{
                            width:'calc(100% - 20px)', padding:10,
                            background:'#333', color:'#fff', border:'none',
                            borderRadius:5, marginBottom:10,
                          }}
                        />
    
                        {/* Amount */}
                        <label>Invoice Amount (€)</label>
                        <input
                          type="text" value={invoiceAmount}
                          onChange={e=>setInvoiceAmount(e.target.value)}
                          style={{
                            width:'calc(100% - 20px)', padding:10,
                            background:'#333', color:'#fff', border:'none',
                            borderRadius:5, marginBottom:10,
                          }}
                        />
    
                        {/* ───────── ARTIST PICKER ───────── */}
                        <label>Filter by Artist (Optional)</label>
                        {!selectedArtist ? (
                          <>
                            <input
                              type="text"
                              placeholder="Search artist…"
                              value={searchArtistTerm}
                              onChange={e=>setSearchArtistTerm(e.target.value)}
                              onFocus={()=>{ setArtistFocus(true); if(!searchArtistTerm) setSearchArtistTerm(''); }}
                              onBlur={()=>setTimeout(()=>setArtistFocus(false),100)}
                              style={{
                                width:'calc(100% - 20px)', padding:10,
                                background:'#333', color:'#fff', border:'none',
                                borderRadius:5, marginBottom:10,
                              }}
                            />
                            {artistFocus && filteredArtists.length>0 && (
                              <div style={{
                                background:'#333', borderRadius:5,
                                maxHeight:160, overflowY:'auto', marginBottom:10,
                              }}>
                                {filteredArtists.map(a=>(
                                  <div
                                    key={a._id}
                                    onMouseDown={()=>{
                                      setSelectedArtist(a);
                                      setSearchArtistTerm(a.name || a.title || '');
                                      setArtistFocus(false);
                                      // Clear project selection when artist changes
                                      setSelectedProject(null);
                                      setSearchTerm('');
                                      setSelectedRelease(null);
                                      setSearchReleaseTerm('');
                                      setSelectedTask(null);
                                      setSearchTaskTerm('');
                                      setTasksForRelease([]);
                                    }}
                                    style={{
                                      padding:10, cursor:'pointer',
                                      borderBottom:'1px solid #555',
                                    }}
                                  >{a.name || a.title}</div>
                                ))}
                              </div>
                            )}
                          </>
                        ) : (
                          <div style={{
                            background:'#333', padding:10, borderRadius:5,
                            marginBottom:10, display:'flex', justifyContent:'space-between',
                            alignItems:'center',
                          }}>
                            <span>Selected Artist: {selectedArtist.name || selectedArtist.title}</span>
                            <button
                              onClick={()=>{
                                setSelectedArtist(null);
                                setSearchArtistTerm('');
                                // Clear project selection when artist is cleared
                                setSelectedProject(null);
                                setSearchTerm('');
                                setSelectedRelease(null);
                                setSearchReleaseTerm('');
                                setSelectedTask(null);
                                setSearchTaskTerm('');
                                setTasksForRelease([]);
                              }}
                              style={{
                                padding:'5px 10px', background:'#555', color:'#fff',
                                border:'none', borderRadius:5,
                              }}
                            >Clear</button>
                          </div>
                        )}
    
                        {/* ───────── PROJECT PICKER ───────── */}
                        <label>Link to Project</label>
                        {!selectedProject ? (
                          <>
                            <input
                              type="text"
                              placeholder="Search project…"
                              value={searchTerm}
                              onChange={e=>setSearchTerm(e.target.value)}
                              onFocus={()=>{ setProjFocus(true); if(!searchTerm) setSearchTerm(''); }}
                              onBlur={()=>setTimeout(()=>setProjFocus(false),100)}
                              style={{
                                width:'calc(100% - 20px)', padding:10,
                                background:'#333', color:'#fff', border:'none',
                                borderRadius:5, marginBottom:10,
                              }}
                            />
                            {projFocus && filteredProjects.length>0 && (
                              <div style={{
                                background:'#333', borderRadius:5,
                                maxHeight:160, overflowY:'auto', marginBottom:10,
                              }}>
                                {filteredProjects.map(p=>(
                                  <div
                                    key={p._id}
                                    onMouseDown={()=>{
                                      setSelectedProject(p);
                                      setSearchTerm(p.title);
                                      setProjFocus(false);
                                      if(p.costs) setInvoiceCost(p.costs);
                                    }}
                                    style={{
                                      padding:10, cursor:'pointer',
                                      borderBottom:'1px solid #555',
                                    }}
                                  >{p.title}</div>
                                ))}
                              </div>
                            )}
                          </>
                        ) : (
                          <div style={{
                            background:'#333', padding:10, borderRadius:5,
                            marginBottom:10, display:'flex', justifyContent:'space-between',
                            alignItems:'center',
                          }}>
                            <span>Selected Project: {selectedProject.title}</span>
                            <button
                              onClick={()=>{
                                setSelectedProject(null); setSearchTerm('');
                                setSelectedRelease(null); setSearchReleaseTerm('');
                                setSelectedTask(null);   setSearchTaskTerm('');
                                setTasksForRelease([]);  setInvoiceCost('');
                                // Keep artist selection when clearing project
                              }}
                              style={{
                                padding:'5px 10px', background:'#555', color:'#fff',
                                border:'none', borderRadius:5,
                              }}
                            >Clear</button>
                          </div>
                        )}
    
                        {/* ───────── RELEASE PICKER ───────── */}
                        {selectedProject && (
                          <>
                            <label>Link to Release</label>
                            {!selectedRelease ? (
                              <>
                                <input
                                  type="text"
                                  placeholder="Search release…"
                                  value={searchReleaseTerm}
                                  onChange={e=>setSearchReleaseTerm(e.target.value)}
                                  onFocus={()=>{ setRelFocus(true);
                                                  if(!searchReleaseTerm)setSearchReleaseTerm(''); }}
                                  onBlur={()=>setTimeout(()=>setRelFocus(false),100)}
                                  style={{
                                    width:'calc(100% - 20px)', padding:10,
                                    background:'#333', color:'#fff', border:'none',
                                    borderRadius:5, marginBottom:10,
                                  }}
                                />
                                {relFocus && filteredReleases.length>0 && (
                                  <div style={{
                                    background:'#333', borderRadius:5,
                                    maxHeight:160, overflowY:'auto', marginBottom:10,
                                  }}>
                                    {filteredReleases.map(r=>(
                                      <div
                                        key={r._id}
                                        onMouseDown={()=>{
                                          setSelectedRelease(r);
                                          setSearchReleaseTerm(r.title);
                                          setRelFocus(false);
                                        }}
                                        style={{
                                          padding:10, cursor:'pointer',
                                          borderBottom:'1px solid #555',
                                        }}
                                      >{r.title}</div>
                                    ))}
                                  </div>
                                )}
                              </>
                            ) : (
                              <div style={{
                                background:'#333', padding:10, borderRadius:5,
                                marginBottom:10, display:'flex',
                                justifyContent:'space-between', alignItems:'center',
                              }}>
                                <span>Selected Release: {selectedRelease.title}</span>
                                <button
                                  onClick={()=>{
                                    setSelectedRelease(null); setSearchReleaseTerm('');
                                    setSelectedTask(null); setSearchTaskTerm('');
                                    setTasksForRelease([]);
                                  }}
                                  style={{
                                    padding:'5px 10px', background:'#555', color:'#fff',
                                    border:'none', borderRadius:5,
                                  }}
                                >Clear</button>
                              </div>
                            )}
                          </>
                        )}
    
                        
    {/* ───────── TASK PICKER ───────── */}
<label>Task</label>

{/* Inline create task (only when we have a project + release) */}
{selectedProject && selectedRelease && (
  <div style={{ display:'flex', gap:8, alignItems:'center', marginBottom:10 }}>
    <input
      value={newTaskName}
      onChange={(e)=>setNewTaskName(e.target.value)}
      placeholder="New task name…"
      style={{
        flex:'1 1 auto',
        padding:10, background:'#333', color:'#fff',
        border:'none', borderRadius:5,
      }}
    />
    <button
  onClick={handleCreateTask}
  disabled={creatingTask || !selectedProject || !selectedRelease || !newTaskName.trim()}
  style={{
    padding:'10px 12px',
    background: creatingTask ? '#555' : '#28a745',
    color:'#fff', border:'none', borderRadius:5,
    cursor: creatingTask ? 'not-allowed' : 'pointer',
    whiteSpace:'nowrap',
  }}
  title="Create task in this release"
>
  {creatingTask ? 'Creating…' : 'Add Task'}
</button>
  </div>
)}
{createTaskError && (
  <div style={{ color:'salmon', marginBottom:8 }}>{createTaskError}</div>
)}

{!selectedTask ? (
  <>
    <input
      placeholder={loadingTasks ? 'Loading tasks…' : 'Search task…'}
      value={searchTaskTerm}
      disabled={loadingTasks}
      onChange={(e)=>setSearchTaskTerm(e.target.value)}
      onFocus={()=>{ setTaskFocus(true); if(!searchTaskTerm) setSearchTaskTerm(''); }}
      onBlur={()=>setTimeout(()=>setTaskFocus(false),100)}
      style={{
        width:'calc(100% - 20px)', padding:10, background:'#333',
        color:'#fff', border:'none', borderRadius:5, marginBottom:10,
      }}
    />
    {taskFocus && filteredTasks.length>0 && (
      <div style={{
        background:'#333', borderRadius:5, marginBottom:10,
        maxHeight:160, overflowY:'auto',
      }}>
        {filteredTasks.map(t=>(
          <div key={t._id}
            onMouseDown={()=>{
              setSelectedTask(t); setSearchTaskTerm(t.name);
              setTaskFocus(false);
            }}
            style={{ padding:10, cursor:'pointer', borderBottom:'1px solid #555' }}
          >{t.name}</div>
        ))}
      </div>
    )}
  </>
) : (
  <div style={{
    background:'#333', padding:10, borderRadius:5, marginBottom:10,
    display:'flex', justifyContent:'space-between', alignItems:'center',
  }}>
    <span>{selectedTask.name}</span>
    <button
      onClick={()=>{ setSelectedTask(null); setSearchTaskTerm(''); }}
      style={{
        padding:'5px 10px', background:'#555', color:'#fff',
        border:'none', borderRadius:5,
      }}
    >Clear</button>
  </div>
)}
                        {/* Cost */}
                        <label>Cost</label>
                        <div style={{
                          display:'flex', flexWrap:'wrap', gap:10, marginBottom:10,
                        }}>
                          {costsOptions.map(opt=>{
                            const sel=invoiceCost===opt.label;
                            return (
                              <div
                                key={opt.label}
                                onClick={()=>setInvoiceCost(opt.label)}
                                style={{
                                  padding:'5px 8px', borderRadius:5, cursor:'pointer',
                                  background:sel?opt.color:'#2c2c2c',
                                  color:'#fff', fontWeight:'bold',
                                }}
                              >{opt.label}</div>
                            );
                          })}
                        </div>
    
                        {/* Payment status */}
                        <label>Payment Status</label>
                        <div style={{ marginBottom:10 }}>
                          <button
                            onClick={()=>{setPaymentStatus('paid'); setPaymentMethod('');}}
                            style={{
                              padding:'10px 20px',
                              background:paymentStatus==='paid'?'#28a745':'#555',
                              color:'#fff', border:'none', borderRadius:5, marginRight:10,
                            }}
                          >Paid</button>
                          <button
                            onClick={()=>{setPaymentStatus('needs_paid'); setPaymentMethod('');}}
                            style={{
                              padding:'10px 20px',
                              background:paymentStatus==='needs_paid'?'orange':'#555',
                              color:'#fff', border:'none', borderRadius:5,
                            }}
                          >Needs to be paid</button>
                        </div>
    
                        {paymentStatus==='paid' && (
                          <div style={{ marginBottom:10 }}>
                            <label>Paid From (Select Method):</label>
                            <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
                              {['Wise','PayPal','Sparkasse'].map(m=>(
                                <button
                                  key={m}
                                  onClick={()=>setPaymentMethod(m)}
                                  style={{
                                    padding:'8px 16px',
                                    background:paymentMethod===m?'#555':'#2c2c2c',
                                    color:'#fff', border:'none', borderRadius:5,
                                  }}
                                >{m}</button>
                              ))}
                            </div>
                          </div>
                        )}
    
                        {/* Buttons */}
                        <div style={{ display:'flex', justifyContent:'flex-end', gap:10 }}>
                          <button onClick={handleCloseModal}
                            style={{
                              padding:'10px 20px', background:'#555',
                              color:'#fff', border:'none', borderRadius:5,
                            }}
                          >Close</button>
                          <button onClick={handleNext}
                            style={{
                              padding:'10px 20px', background:'#555',
                              color:'#fff', border:'none', borderRadius:5,
                            }}
                          >Next</button>
                        </div>
                      </>
                    )}
    
                    {/* ===========================================================
                       STEP 4 — EMAIL SEND
                       ======================================================= */}
                    {step===4 && (
                      <>
                        <h2 style={{ margin:'0 0 10px' }}>Send Email</h2>
    
                        <label>Recipient</label>
                        <input
                          type="text" value={recipient}
                          onChange={e=>setRecipient(e.target.value)}
                          style={{
                            width:'calc(100% - 20px)', padding:10,
                            background:'#333', color:'#fff', border:'none',
                            borderRadius:5, marginBottom:10,
                          }}
                        />
    
                        <label>CC</label>
                        <input
                          type="text" value={cc}
                          onChange={e=>setCc(e.target.value)}
                          style={{
                            width:'calc(100% - 20px)', padding:10,
                            background:'#333', color:'#fff', border:'none',
                            borderRadius:5, marginBottom:10,
                          }}
                        />
    
                        <label>Subject</label>
                        <input
                          type="text" value={emailSubject}
                          onChange={e=>setEmailSubject(e.target.value)}
                          style={{
                            width:'calc(100% - 20px)', padding:10,
                            background:'#333', color:'#fff', border:'none',
                            borderRadius:5, marginBottom:10,
                          }}
                        />
    
                        <label>Body</label>
                        <textarea
                          value={emailBody}
                          onChange={e=>setEmailBody(e.target.value)}
                          style={{
                            width:'calc(100% - 20px)', height:100, padding:10,
                            background:'#333', color:'#fff', border:'none',
                            borderRadius:5, marginBottom:10,
                          }}
                        />
    
                        <div style={{ display:'flex', justifyContent:'flex-end', gap:10 }}>
                          <button onClick={handleCloseModal}
                            style={{
                              padding:'10px 20px', background:'#555',
                              color:'#fff', border:'none', borderRadius:5,
                            }}
                          >Close</button>
                          <button
                            onClick={handleSendEmail}
                            disabled={emailSending}
                            style={{
                              padding:'10px 20px', background:'#555',
                              color:'#fff', border:'none', borderRadius:5,
                            }}
                          >{emailSending?'Sending…':'Send Email'}</button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
    
              {/* ========= TABLE ========= */}
        <InvoiceDatabase
  invoices={invoices}
  projects={projects}
  releases={releasesForFilter.length ? releasesForFilter : releases}
  handleInvoiceStatusChange={handleInvoiceStatusChange}
  reloadInvoices={reloadInvoices}
  disableClientPaging={true}
  processed={showProcessed}
  onToggleProcessed={async () => {
    const next = !showProcessed;
    setShowProcessed(next);
    setLoadingInvoices(true);
    try {
      await fetchInvoicesPage({ pageArg: 1, append: false, processed: next });
    } finally {
      setLoadingInvoices(false);
    }
  }}
/>

        {/* ========= Load More BELOW the table ========= */}
        {invoices.length < total && (
          <div style={{ marginTop: 20, textAlign: 'center' }}>
            <button
              onClick={() =>
                fetchInvoicesPage({
                  pageArg: page + 1,
                  append: true,
                  processed: showProcessed,
                })
              }
              disabled={loadingPage}
              style={{
                background: '#333',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: 8,
                cursor: loadingPage ? 'not-allowed' : 'pointer',
                opacity: loadingPage ? 0.7 : 1,
              }}
            >
              {loadingPage ? 'Loading…' : 'Load more'}
            </button>
          </div>
        )}
            </>
          )}
        </div>
      );
    };
    
    export default Invoices;